/**
 * "Get directions" link for a business.
 *
 * One https URL covers every platform: on desktop it opens Google Maps in the
 * browser; on Android it is an App Link, so it is handed to the Google Maps app
 * (or whichever app the user has made the default handler); on iOS it is a
 * universal link that opens the Google Maps app when installed and Safari
 * otherwise.
 *
 * Custom schemes (`geo:`, `waze://`, `comgooglemaps://`) would name specific
 * apps and, on Android, raise the system app chooser — but they are silently
 * dropped inside the Flutter WebView that wraps the mobile PWA unless the native
 * side forwards them, so a plain https URL is the one form that never dead-ends.
 */

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * True when the pair can actually be navigated to. Both `Business.lat/lng` and
 * the listing payload default to 0, so (0, 0) is treated as "not set" rather
 * than as the Atlantic.
 */
export function hasCoordinates(lat: unknown, lng: unknown): boolean {
  const latitude = toNumber(lat);
  const longitude = toNumber(lng);
  if (latitude === null || longitude === null) {
    return false;
  }
  if (Math.abs(latitude) > MAX_LATITUDE || Math.abs(longitude) > MAX_LONGITUDE) {
    return false;
  }
  return latitude !== 0 || longitude !== 0;
}

/**
 * Coordinates are preferred because they route exactly; the typed address is the
 * fallback. Returns '' when there is nothing to navigate to, which callers use
 * to render the address as plain text instead of a link.
 */
export function directionsUrl(lat: unknown, lng: unknown, address?: string | null): string {
  const destination = hasCoordinates(lat, lng)
    ? `${toNumber(lat)},${toNumber(lng)}`
    : (address ?? '').toString().trim();
  if (!destination) {
    return '';
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}
